import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Newspaper, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import Footer from '../components/sections/Footer';
import SEO from '../components/SEO';
import { getOrganizationSchema, getBreadcrumbSchema } from '../seo/schemas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PER_PAGE = 6;

const News = () => {
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const [news, setNews] = useState([]);
  const [category, setCategory] = useState('company');
  const [page, setPage] = useState(1);

  const t = (en, zh) => isZh ? (zh || en) : (en || zh);

  useEffect(() => {
    const load = async () => {
      try { const r = await axios.get(`${API}/admin/news`); setNews(r.data.news || []); } catch {}
    };
    load();
  }, []);

  const filtered = news.filter(n => n.category === category);
  const priorityOrder = { high: 0, medium: 1, low: 2 };
  const sorted = [...filtered].sort((a, b) => (priorityOrder[a.priority] || 2) - (priorityOrder[b.priority] || 2));
  const featured = sorted.slice(0, 3);
  const rest = sorted.slice(3);
  const totalPages = Math.max(1, Math.ceil(rest.length / PER_PAGE));
  const paged = rest.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const tabs = [
    { id: 'company', icon: Building2, label: isZh ? '企业动态' : 'Company News' },
    { id: 'industry', icon: Newspaper, label: isZh ? '行业资讯' : 'Industry News' },
  ];

  const title = (n) => t(n.title_en, n.title_zh) || n.title || '';
  const desc = (n) => t(n.description_en, n.description_zh) || n.description || '';

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SEO
        title={isZh ? 'ALIGCUS 新闻中心 | 加州 ADU 行业资讯 · 公司动态 · 法规更新' : 'ALIGCUS News Center | California ADU Industry News, Company Updates & Regulation Changes'}
        description={isZh ? '关注 ALIGCUS 阿里建筑最新动态、加州 ADU 行业资讯与法规变化。我们与未来同行，让您的建房之旅更轻松。' : 'Stay updated with ALIGCUS Construction news, California ADU industry trends, and regulation changes. Walking with the future, making your building journey easier.'}
        canonical={isZh ? 'https://www.aligcus.com/zh/news' : 'https://www.aligcus.com/news'}
        lang={isZh ? 'zh' : 'en'}
        alternates={[
          { lang: 'en', url: 'https://www.aligcus.com/news' },
          { lang: 'zh-CN', url: 'https://www.aligcus.com/zh/news' },
        ]}
        schemas={[
          getOrganizationSchema(isZh ? 'zh' : 'en'),
          getBreadcrumbSchema([
            { name: isZh ? '首页' : 'Home', url: isZh ? 'https://www.aligcus.com/zh/' : 'https://www.aligcus.com/' },
            { name: isZh ? '新闻' : 'News', url: isZh ? 'https://www.aligcus.com/zh/news' : 'https://www.aligcus.com/news' },
          ]),
        ]}
      />
      <section className="relative flex items-center justify-center" style={{ height: '300px' }}>
        <div className="absolute inset-0 bg-gray-800">
          <img src="https://customer-assets.emergentagent.com/job_9ca81a2a-0a4d-4b76-b02e-3997c1e64651/artifacts/d5801gqq_ScreenShot_2026-03-11_160721_854.png" alt="" className="w-full h-full object-cover opacity-40" />
        </div>
        <div className="relative text-center px-4 sm:px-6">
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold text-white mb-3 sm:mb-4">{isZh ? '新闻中心' : 'News Center'}</h1>
          <p className="text-sm sm:text-lg text-white/70">{isZh ? '与未来同行，让我们的生活更美好' : 'Walking with the future, making life better'}</p>
        </div>
      </section>

      <div className="flex justify-center gap-4 py-6 bg-gray-50 border-b">
        {tabs.map(tb => (
          <button key={tb.id} onClick={() => { setCategory(tb.id); setPage(1); }}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm transition-all ${category === tb.id ? 'bg-red-600 text-white shadow-md' : 'bg-white text-gray-600 border hover:bg-gray-100'}`}>
            <tb.icon className="w-4 h-4" />{tb.label}
          </button>
        ))}
      </div>

      <main className="flex-grow">
        {featured.length > 0 && (
          <section className="bg-gray-50 py-10">
            <div className="max-w-6xl mx-auto px-6">
              <div className="grid md:grid-cols-3 gap-6">
                {featured.map(n => (
                  <div key={n.id} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow group">
                    {n.image && <div className="h-48 overflow-hidden"><img src={n.image} alt={title(n)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>}
                    <div className="p-5">
                      <p className="text-xs text-red-600 font-semibold mb-2">{n.date}</p>
                      <h3 className="font-bold text-gray-900 mb-2 line-clamp-2 text-sm">{title(n)}</h3>
                      <p className="text-gray-500 text-xs mb-3 line-clamp-3">{desc(n)}</p>
                      {n.tags && <div className="flex flex-wrap gap-1 mb-3">{n.tags.split(',').map((tag, i) => <span key={i} className="bg-red-50 text-red-600 text-xs px-2 py-0.5 rounded">{tag.trim()}</span>)}</div>}
                      {n.link && <a href={n.link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-red-600 text-xs font-semibold hover:text-red-700">{isZh ? '了解更多' : 'Learn More'} <ExternalLink className="w-3 h-3" /></a>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="py-8">
          <div className="max-w-6xl mx-auto px-6">
            <div className="divide-y">
              {paged.map(n => (
                <div key={n.id} className="py-5 flex gap-5 items-start hover:bg-gray-50 -mx-4 px-4 rounded-lg transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-xs text-red-600 font-bold">{n.date}</span>
                      <h3 className="font-bold text-gray-900 text-base">{title(n)}</h3>
                    </div>
                    <p className="text-gray-500 text-sm mb-2 line-clamp-2">{desc(n)}</p>
                    {n.tags && <div className="flex flex-wrap gap-1 mb-2">{n.tags.split(',').map((tag, i) => <span key={i} className="bg-red-50 text-red-600 text-xs px-2 py-0.5 rounded">{tag.trim()}</span>)}</div>}
                  </div>
                  {n.link && <a href={n.link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-red-600 text-xs font-semibold hover:text-red-700 whitespace-nowrap flex-shrink-0">{isZh ? '了解更多' : 'Learn More'} <ExternalLink className="w-3 h-3" /></a>}
                </div>
              ))}
              {paged.length === 0 && <p className="text-center text-gray-400 py-16">{isZh ? '暂无新闻' : 'No news yet'}</p>}
            </div>

            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-8">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="w-8 h-8 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-100 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
                {[...Array(Math.min(totalPages, 7))].map((_, i) => {
                  const p = i + 1;
                  return <button key={p} onClick={() => setPage(p)} className={`w-8 h-8 rounded-full text-sm font-medium ${page === p ? 'bg-red-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>{p}</button>;
                })}
                {totalPages > 7 && <span className="text-gray-400">...</span>}
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="w-8 h-8 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-100 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default News;
